import { supabase } from '../config/supabase.js'
import { navigateToPerson } from '../router/router.js'
import './family-tree.css'

// Module state
let allPeople = []
let allFamilies = []
let allFamilyChildren = []
let treeData = []
let expandedNodes = new Set()
let searchTerm = ''
let highlightedPersonId = null

/**
 * Initialize the family tree section on the homepage
 */
export async function initFamilyTree() {
  const container = document.getElementById('family-tree-section')
  if (!container) return

  container.innerHTML = `
    <div class="tree-header">
      <h2>🌳 Family Tree</h2>
      <div class="tree-controls">
        <button id="expand-all-btn">Expand All</button>
        <button id="collapse-all-btn">Collapse All</button>
      </div>
    </div>
    <div id="search-result-info" class="search-result-info" style="display: none;">
      <span id="search-result-text"></span>
      <button class="clear-search-btn" id="clear-tree-search-btn">Show Full Tree</button>
    </div>
    <div id="tree-container" class="tree-container loading">
      Loading family tree...
    </div>
    <div class="tree-stats" id="tree-stats"></div>
  `

  // Set up event listeners
  document.getElementById('expand-all-btn').addEventListener('click', expandAll)
  document.getElementById('collapse-all-btn').addEventListener('click', collapseAll)
  document.getElementById('clear-tree-search-btn').addEventListener('click', clearTreeSearch)

  // Load data and render
  await loadTreeData()
  renderTree()
}

/**
 * Load all necessary data from Supabase
 */
async function loadTreeData() {
  try {
    // Fetch all people with names and events
    const { data: people, error: peopleError } = await supabase
      .from('person')
      .select(`
        id,
        sex,
        is_living,
        person_name (
          full_name,
          given_name,
          surname,
          is_primary
        ),
        event (
          event_type,
          date_text
        )
      `)

    if (peopleError) throw peopleError

    // Fetch all families
    const { data: families, error: familiesError } = await supabase
      .from('family')
      .select('id, partner1_id, partner2_id')

    if (familiesError) throw familiesError

    // Fetch all family_child relationships
    const { data: familyChildren, error: fcError } = await supabase
      .from('family_child')
      .select('family_id, child_id')

    if (fcError) throw fcError

    allPeople = people
    allFamilies = families
    allFamilyChildren = familyChildren

    // Build the tree structure
    buildTree()

  } catch (error) {
    console.error('Error loading tree data:', error)
    document.getElementById('tree-container').innerHTML = `
      <div class="tree-empty">Error loading family tree: ${error.message}</div>
    `
  }
}

/**
 * Build tree structure from flat data
 */
function buildTree() {
  // Find root ancestors (people who are not children in any family)
  const childIds = new Set(allFamilyChildren.map(fc => fc.child_id))
  const rootAncestors = allPeople.filter(p => !childIds.has(p.id))

  // Sort roots by birth year (oldest first)
  rootAncestors.sort((a, b) => {
    const aYear = getBirthYear(a)
    const bYear = getBirthYear(b)
    return (aYear || 9999) - (bYear || 9999)
  })

  // Group roots by their family (couples together)
  const processedIds = new Set()
  treeData = []

  rootAncestors.forEach(person => {
    if (processedIds.has(person.id)) return

    // Find if this person has a spouse who is also a root
    const family = allFamilies.find(f => 
      f.partner1_id === person.id || f.partner2_id === person.id
    )

    if (family) {
      const spouseId = family.partner1_id === person.id ? family.partner2_id : family.partner1_id
      const spouse = rootAncestors.find(p => p.id === spouseId)

      if (spouse && !processedIds.has(spouseId)) {
        // Both are root ancestors - add as couple
        processedIds.add(person.id)
        processedIds.add(spouseId)
        treeData.push(buildPersonNode(person, family))
      } else if (!processedIds.has(person.id)) {
        // Single root or spouse has parents
        processedIds.add(person.id)
        treeData.push(buildPersonNode(person, family))
      }
    } else {
      // No family found
      processedIds.add(person.id)
      treeData.push(buildPersonNode(person, null))
    }
  })

  // Default expand first level
  treeData.forEach(node => {
    if (node.children && node.children.length > 0) {
      expandedNodes.add(node.id)
    }
  })
}

/**
 * Recursively build a person node with their descendants
 */
function buildPersonNode(person, family) {
  const node = {
    id: person.id,
    person: person,
    spouse: null,
    children: [],
    familyId: family?.id
  }

  // Get spouse
  if (family) {
    const spouseId = family.partner1_id === person.id ? family.partner2_id : family.partner1_id
    if (spouseId) {
      node.spouse = allPeople.find(p => p.id === spouseId)
    }

    // Get children
    const childLinks = allFamilyChildren.filter(fc => fc.family_id === family.id)
    const children = childLinks
      .map(fc => allPeople.find(p => p.id === fc.child_id))
      .filter(Boolean)
      .sort((a, b) => {
        const aYear = getBirthYear(a)
        const bYear = getBirthYear(b)
        return (aYear || 9999) - (bYear || 9999)
      })

    // Recursively build children nodes
    children.forEach(child => {
      // Find family where this child is a partner
      const childFamily = allFamilies.find(f => 
        f.partner1_id === child.id || f.partner2_id === child.id
      )
      node.children.push(buildPersonNode(child, childFamily))
    })
  }

  return node
}

/**
 * Render the tree to the DOM
 */
function renderTree(filteredTree = null) {
  const container = document.getElementById('tree-container')
  const dataToRender = filteredTree || treeData

  if (dataToRender.length === 0) {
    container.innerHTML = '<div class="tree-empty">No family members found</div>'
    container.classList.remove('loading')
    return
  }

  container.innerHTML = dataToRender.map(node => renderNode(node, 0)).join('')
  container.classList.remove('loading')

  // Add click handlers
  addTreeEventListeners()

  // Update stats
  updateTreeStats(dataToRender)

  // Scroll to highlighted person if exists
  if (highlightedPersonId) {
    setTimeout(() => {
      const highlighted = container.querySelector('.highlighted')
      if (highlighted) {
        highlighted.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    }, 100)
  }
}

/**
 * Render a single tree node
 */
function renderNode(node, level) {
  const person = node.person
  const hasChildren = node.children && node.children.length > 0
  const isExpanded = expandedNodes.has(node.id)
  const isHighlighted = highlightedPersonId === node.id

  const primaryName = getPrimaryName(person)
  const birthYear = getBirthYear(person)
  const deathYear = getDeathYear(person)

  let datesDisplay = ''
  if (birthYear || deathYear) {
    datesDisplay = `(${birthYear || '?'} - ${person.is_living ? 'present' : (deathYear || '?')})`
  }

  // Spouse info
  let spouseHtml = ''
  if (node.spouse) {
    const spouseName = getPrimaryName(node.spouse)
    const spouseGender = node.spouse.sex === 'M' ? 'male' : node.spouse.sex === 'F' ? 'female' : 'unknown'
    spouseHtml = `
      <span class="spouse-connector">⚭</span>
      <span class="spouse-info">
        <span class="gender-indicator ${spouseGender}"></span>
        <span class="person-name" data-person-id="${node.spouse.id}">${escapeHtml(spouseName)}</span>
      </span>
    `
  }

  const genderClass = person.sex === 'M' ? 'male' : person.sex === 'F' ? 'female' : 'unknown'
  const statusClass = person.is_living ? 'living' : 'deceased'
  const highlightClass = isHighlighted ? 'highlighted' : ''

  const childrenCountHtml = hasChildren 
    ? `<span class="children-count">(${node.children.length} ${node.children.length === 1 ? 'child' : 'children'})</span>`
    : ''

  let childrenHtml = ''
  if (hasChildren && isExpanded) {
    childrenHtml = node.children.map(child => renderNode(child, level + 1)).join('')
  }

  return `
    <div class="tree-node" data-node-id="${node.id}">
      <div class="tree-node-content ${highlightClass}">
        <span class="tree-toggle ${hasChildren ? (isExpanded ? 'expanded' : '') : 'empty'}" 
              data-node-id="${node.id}" 
              ${hasChildren ? '' : 'style="visibility: hidden;"'}>
          ▶
        </span>
        <span class="gender-indicator ${genderClass}"></span>
        <div class="person-info">
          <span class="person-name" data-person-id="${person.id}">${escapeHtml(primaryName)}</span>
          <span class="person-dates">${datesDisplay}</span>
          ${spouseHtml}
          ${childrenCountHtml}
        </div>
      </div>
      ${childrenHtml}
    </div>
  `
}

/**
 * Add event listeners to tree elements
 */
function addTreeEventListeners() {
  // Toggle expand/collapse
  document.querySelectorAll('.tree-toggle:not(.empty)').forEach(toggle => {
    toggle.addEventListener('click', (e) => {
      e.stopPropagation()
      const nodeId = toggle.dataset.nodeId
      if (expandedNodes.has(nodeId)) {
        expandedNodes.delete(nodeId)
      } else {
        expandedNodes.add(nodeId)
      }
      renderTree()
    })
  })

  // Navigate to person on name click
  document.querySelectorAll('.person-name').forEach(nameEl => {
    nameEl.addEventListener('click', (e) => {
      e.stopPropagation()
      const personId = nameEl.dataset.personId
      if (personId) {
        navigateToPerson(personId)
      }
    })
  })
}

/**
 * Search the tree and show filtered view
 */
export function searchTree(term) {
  searchTerm = term.toLowerCase().trim()
  highlightedPersonId = null

  if (!searchTerm) {
    clearTreeSearch()
    return
  }

  // Find matching person
  const matchedPerson = allPeople.find(p => {
    const name = getPrimaryName(p).toLowerCase()
    return name.includes(searchTerm)
  })

  if (!matchedPerson) {
    document.getElementById('search-result-info').style.display = 'flex'
    document.getElementById('search-result-text').textContent = `No match found for "${term}"`
    renderTree([])
    return
  }

  highlightedPersonId = matchedPerson.id

  // Find parents (one level up)
  const parentFamilyLink = allFamilyChildren.find(fc => fc.child_id === matchedPerson.id)
  let rootPerson = matchedPerson
  let rootFamily = null

  if (parentFamilyLink) {
    const parentFamily = allFamilies.find(f => f.id === parentFamilyLink.family_id)
    if (parentFamily) {
      // Use parent as root
      const parent = allPeople.find(p => p.id === parentFamily.partner1_id)
      if (parent) {
        rootPerson = parent
        rootFamily = parentFamily
      }
    }
  } else {
    // No parents - find the person's own family
    rootFamily = allFamilies.find(f => 
      f.partner1_id === matchedPerson.id || f.partner2_id === matchedPerson.id
    )
  }

  // Build filtered tree from this root
  const filteredNode = buildPersonNode(rootPerson, rootFamily)
  
  // Expand all nodes to show the matched person
  expandNodePath(filteredNode, matchedPerson.id)

  // Show search result info
  document.getElementById('search-result-info').style.display = 'flex'
  document.getElementById('search-result-text').textContent = 
    `Showing tree for: ${getPrimaryName(matchedPerson)}`

  renderTree([filteredNode])
}

/**
 * Expand all nodes in path to target person
 */
function expandNodePath(node, targetId) {
  if (node.id === targetId) {
    return true
  }

  if (node.spouse && node.spouse.id === targetId) {
    expandedNodes.add(node.id)
    return true
  }

  for (const child of node.children) {
    if (expandNodePath(child, targetId)) {
      expandedNodes.add(node.id)
      return true
    }
  }

  return false
}

/**
 * Clear search and show full tree
 */
function clearTreeSearch() {
  searchTerm = ''
  highlightedPersonId = null
  document.getElementById('search-result-info').style.display = 'none'
  
  // Reset to default expanded state
  expandedNodes.clear()
  treeData.forEach(node => {
    if (node.children && node.children.length > 0) {
      expandedNodes.add(node.id)
    }
  })
  
  renderTree()
}

/**
 * Expand all nodes
 */
function expandAll() {
  const addAllNodes = (nodes) => {
    nodes.forEach(node => {
      if (node.children && node.children.length > 0) {
        expandedNodes.add(node.id)
        addAllNodes(node.children)
      }
    })
  }
  addAllNodes(treeData)
  renderTree()
}

/**
 * Collapse all nodes
 */
function collapseAll() {
  expandedNodes.clear()
  renderTree()
}

/**
 * Update tree statistics display
 */
function updateTreeStats(data) {
  const statsEl = document.getElementById('tree-stats')
  
  // Count total people in displayed tree
  let totalPeople = 0
  let generations = 0
  
  const countNodes = (nodes, level) => {
    nodes.forEach(node => {
      totalPeople++
      if (node.spouse) totalPeople++
      if (level > generations) generations = level
      if (node.children) {
        countNodes(node.children, level + 1)
      }
    })
  }
  
  countNodes(data, 1)
  
  statsEl.innerHTML = `
    <div class="tree-stat">
      <span>👥</span>
      <strong>${totalPeople}</strong> people shown
    </div>
    <div class="tree-stat">
      <span>📊</span>
      <strong>${generations}</strong> generations
    </div>
    <div class="tree-stat">
      <span>🌿</span>
      <strong>${data.length}</strong> root ${data.length === 1 ? 'branch' : 'branches'}
    </div>
  `
}

// Helper functions
function getPrimaryName(person) {
  if (!person || !person.person_name) return 'Unknown'
  const primary = person.person_name.find(n => n.is_primary)
  return primary?.full_name || person.person_name[0]?.full_name || 'Unknown'
}

function getBirthYear(person) {
  if (!person || !person.event) return null
  const birth = person.event.find(e => e.event_type === 'birth')
  if (!birth || !birth.date_text) return null
  const match = birth.date_text.match(/(\d{4})/)
  return match ? match[1] : null
}

function getDeathYear(person) {
  if (!person || !person.event) return null
  const death = person.event.find(e => e.event_type === 'death')
  if (!death || !death.date_text) return null
  const match = death.date_text.match(/(\d{4})/)
  return match ? match[1] : null
}

function escapeHtml(text) {
  if (!text) return ''
  const div = document.createElement('div')
  div.textContent = text
  return div.innerHTML
}
